import React, { Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Table, Button, notification, DatePicker, Select, Input } from 'antd';
import { DownloadOutlined, FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import dayjs from 'dayjs';
import { AuthContext } from 'context/AuthContext';
import reportService from 'services/report.service';
import branchService from 'services/branch.service';
import { formatDateForClient, formatDateForServer, formatMoney } from 'helpers/utils';
import { storage } from 'helpers';
import download from 'js-file-download';

const { RangePicker } = DatePicker;
const { Option } = Select;

const renderFilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
  <div style={{ padding: 8 }}>
    <Input
      placeholder="Fatura No Ara"
      value={selectedKeys[0]}
      onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
      onPressEnter={() => confirm()}
      style={{ width: 188, marginBottom: 8, display: 'block' }}
    />
    <Button
      type="primary"
      onClick={() => confirm()}
      icon={<SearchOutlined />}
      size="small"
      style={{ width: 90, marginRight: 8 }}
    >
      Ara
    </Button>
    <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
      Temizle
    </Button>
  </div>
);

class PayeInvoice extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      reports: [],
      branches: [],
      selectedBranch: '0',
      loading: false,
      startDate: formatDateForClient(dayjs().subtract(365, 'days')),
      endDate: formatDateForClient(),
      currentMerch: context?.selectedMerch,
      pdfDownloading: false,
      columns: [
        {
          title: <div className="table-content-calibri-bold-11">Takip Numarası</div>,
          dataIndex: 'TakipNo',
          align: 'center',
          key: 'TakipNo',
          width: 150,
          render: (text, row) => (
            <div>
              <span className="table-title-calibri-bold-12">{row.TakipNo}</span> <br />
              <span className="table-content-calibri-light-9">
                İlişkili Fatura: {row.RelatedInvoiceNumber}
              </span>
            </div>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Şube Bilgisi</div>,
          dataIndex: 'SubeOrMerkez',
          key: 'SubeOrMerkez',
          align: 'center',
          width: 260,
          render: (text, row) => (
            <div>
              <span className="table-title-calibri-bold-12">{row.BranchName}</span> -{' '}
              <span className="table-title-calibri-bold-12">{row.BranchId}</span>
              <br />
              <span className="table-content-calibri-light-10">
                Fatura Kesme Yöntemi: {row.SubeOrMerkez}
              </span>
            </div>
          ),
        },
        {
          title: <div className="table-content-calibri-bold-11">Fatura Tarihi</div>,
          dataIndex: 'InvoiceDate',
          key: 'InvoiceDate',
          align: 'center',
          width: 150,
          render: (text, row) => (
            <span className="table-title-calibri-bold-12">
              {row.InvoiceDate ? dayjs(row.InvoiceDate).format('DD.MM.YYYY') : '?'}
            </span>
          ),
        },
        {
          title: <div className="table-title-calibri-bold-12">Fatura Numarası / Türü</div>,
          dataIndex: 'InvoiceNumber',
          key: 'InvoiceNumber',
          align: 'center',
          width: 200,
          onFilter: (value, record) => record?.InvoiceNumber?.toString()?.indexOf(value) === 0,
          filterDropdown: renderFilterDropdown,
          render: (text, row) => (
            <div>
              <span className="table-title-calibri-bold-12">{row.InvoiceNumber}</span> <br />
              <span className="table-content-calibri-light-12">{row.InvoiceType}</span>
            </div>
          ),
        },
        {
          title: <div className="table-content-calibri-bold-11">Fatura Tutarı</div>,
          dataIndex: 'InvoiceAmount',
          key: 'InvoiceAmount',
          align: 'center',
          width: 150,
          render: (text, row) => (
            <span className="table-title-calibri-bold-12">
              {row.InvoiceAmount ? formatMoney(row.InvoiceAmount) : '?'} TL
            </span>
          ),
        },
        {
          title: '',
          dataIndex: 'InvoiceURL',
          key: 'InvoiceURL',
          align: 'center',
          width: 120,
          render: (row, record) => (
            <Button
              loading={this.state.pdfDownloading === record?.InvoiceNumber}
              disabled={this.state.pdfDownloading === record?.InvoiceNumber}
              key="1"
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={async () => {
                try {
                  if (context?.selectedMerch?.number && record?.InvoiceNumber) {
                    this.setState({ pdfDownloading: record?.InvoiceNumber });
                    const body = {
                      type: 'INDIVIDUAL',
                      fromUrl: record?.InvoiceURL,
                    };
                    const payload = await reportService.getInvoiceReportsSelectedPdf(
                      context?.selectedMerch?.number,
                      body,
                    );
                    download(
                      payload,
                      `FATURALAR-${context?.selectedMerch?.number}-${dayjs().format('DD-MM-YYYY')}.pdf`,
                    );
                    this.setState({ pdfDownloading: false });
                  } else {
                    notification.error({
                      message: 'HATA !',
                      description: 'Hatalı kayıt. Lütfen yöneticiniz ile iletişime geçiniz.',
                    });
                  }
                } catch (error) {
                  this.setState({ pdfDownloading: false });
                  notification.error({
                    message: 'İşlem Başarısız',
                    description: error,
                  });
                }
              }}
            >
              PDF
            </Button>
          ),
        },
      ],
    };
  }

  componentDidMount() {
    this.getReports();
    this.getCurrentMerchBranches();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState?.currentMerch?.number !== this.context?.selectedMerch?.number &&
      this.state?.currentMerch?.number === prevState.currentMerch?.number
    ) {
      this.getReports();
      this.getCurrentMerchBranches();
    }
  }

  getReports = () => {
    this.setState({ loading: true, currentMerch: this.context?.selectedMerch }, async () => {
      try {
        const { currentMerch, endDate, startDate, selectedBranch } = this.state;
        if (currentMerch?.number) {
          const { payload } = await reportService.getPayeInvoicesReports(
            currentMerch?.number,
            formatDateForServer(startDate),
            formatDateForServer(endDate),
            selectedBranch,
          );
          this.setState({ loading: false, reports: payload });
        }
      } catch (error) {
        this.setState({ loading: false, reports: [] });
        notification.error({
          message: 'İşlem Başarısız',
          description: error,
        });
      }
    });
  };

  getCurrentMerchBranches = async () => {
    try {
      await this.setState({ loading: true, currentMerch: this.context?.selectedMerch });
      const number = this.context?.selectedMerch?.number;
      if (number) {
        const { payload } = await branchService.getBranchesByMerch(number);
        this.setState({ loading: false, branches: payload });
      }
    } catch (error) {
      this.setState({ loading: false });
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  onDateChange = (dates) => {
    if (!dates || dates.length !== 2) return;
    const startDate = formatDateForClient(dates[0]);
    const endDate = formatDateForClient(dates[1]);
    this.setState({ startDate, endDate }, () => {
      storage.set({ startDate, endDate }, 'date-filters');
      this.getReports();
    });
  };

  onBranchChange = (selectedBranch) => {
    if (!selectedBranch) {
      // eslint-disable-next-line no-param-reassign
      selectedBranch = 0;
    }
    this.setState({ selectedBranch }, () => {
      storage.set({ selectedBranch }, 'branch-filters');
      this.getReports();
    });
  };

  renderBranchOptions = () => {
    const { branches } = this.state;
    return branches?.map((branch) => (
      <Option key={branch.BranchId} value={branch.BranchId}>
        {branch.BranchTitle}
      </Option>
    ));
  };

  downloadExcel = async () => {
    try {
      this.setState({ downloading: true });
      const { currentMerch, startDate, endDate, selectedBranch } = this.state;
      const data = await reportService.getPayeInvoicesReportsExcel(
        currentMerch?.number,
        formatDateForServer(startDate),
        formatDateForServer(endDate),
        selectedBranch,
      );
      download(data, `PayeFaturalarim-${currentMerch?.number}-${dayjs().format('DD-MM-YYYY')}.xls`);
      this.setState({ downloading: false });
    } catch (error) {
      this.setState({ loading: false, downloading: false });
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  render() {
    const { reports, columns, loading, startDate, endDate, downloading } = this.state;

    return (
      <PageContainer
        title="Paye Tarafından Kesilen Faturalar"
        extra={[
          <RangePicker
            allowClear={false}
            defaultValue={[dayjs(startDate, 'DD.MM.YYYY'), dayjs(endDate, 'DD.MM.YYYY')]}
            key="eod"
            onChange={this.onDateChange}
            format="DD.MM.YYYY"
          />,
          <Select
            key="branch"
            loading={loading}
            defaultValue={0}
            onChange={this.onBranchChange}
            style={{ width: 200 }}
          >
            <Option value={0} key="ALL">
              Tüm Şubeler
            </Option>
            {this.renderBranchOptions()}
          </Select>,
          <Button
            loading={downloading}
            disabled={loading || downloading}
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={async () => {
              await this.downloadExcel();
            }}
          >
            İndir
          </Button>,
        ]}
      >
        <Table
          loading={loading}
          dataSource={reports}
          columns={columns}
          pagination={false}
          rowKey={(record, index) => index}
        />
      </PageContainer>
    );
  }
}

PayeInvoice.contextType = AuthContext;

const PayeInvoiceWrapper = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  return <PayeInvoice {...props} navigate={navigate} params={params} />;
};

export default PayeInvoiceWrapper;
